import { Paula as WrappedPaula } from '@generated/edukita/paula-backend/v1/service.pb';
import { PythagorasV2 as WrappedPythagoras } from '@generated/edukita/paula-backend/v2/pythagoras_service.pb';
import { ScheduleV2 as WrappedScheduleV2 } from '@generated/edukita/paula-backend/v2/schedule.pb';
import { LevelTrackerService as WrappedLevelTracker } from '@generated/edukita/paula-backend/v3/level_tracker_service.pb';
import { StudentTrackerService as WrappedStudentTracker } from '@generated/edukita/paula-backend/v3/student_tracker.pb';
import { LearningSummaryService as WrappedLearningSummary } from '@generated/edukita/paula-backend/v3/learning_summary_service.pb';
import { TeacherFeedbackV3Service as WrappedTeacherFeedbackV3 } from '@generated/edukita/paula-backend/v3/teacher_feedback_v3_service.pb';
import { TeacherFeedbackMathService as WrappedTeacherFeedbackMath } from '@generated/edukita/paula-backend/v3/teacher_feedback_math_v3_service.pb';
import { BookTrackerService as WrappedBookTracker } from '@generated/edukita/paula-backend/v3/book_tracker_v3_service.pb';

import {
    buildCookieString,
    getJWTRemainingSeconds,
    REFRESH_TOKEN_MAX_AGE,
} from '@utils/cookieConfig';

import config from '@utils/config';

const API_ENDPOINT = config.backendURI;
const prefix = { pathPrefix: API_ENDPOINT };

type FetchInit = NonNullable<Parameters<typeof fetch>[1]>;
type HeaderInput = FetchInit['headers'];

const getCookie = (name: string): string | null => {
    const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
};

const headersToRecord = (headers?: HeaderInput): Record<string, string> => {
    if (!headers) return {};

    if (typeof Headers !== 'undefined' && headers instanceof Headers) {
        const result: Record<string, string> = {};
        headers.forEach((value, key) => {
            result[key] = value;
        });
        return result;
    }

    if (Array.isArray(headers)) {
        return headers.reduce<Record<string, string>>((acc, [key, value]) => {
            acc[key] = value;
            return acc;
        }, {});
    }

    return headers as Record<string, string>;
};

const getAuthHeaders = (): Record<string, string> => {
    const token = getCookie('accessToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
};

const checkError = (response: any): any => {
    if (response.code != null) {
        throw new Error("Something's Wrong");
    }
    return response;
};

const buildInitReq = (initReq?: any): any => {
    const authHeaders = getAuthHeaders();
    const mergedHeaders = {
        ...authHeaders,
        ...headersToRecord(initReq?.headers),
    };
    return {
        ...prefix,
        ...(initReq ?? {}),
        headers:
            Object.keys(mergedHeaders).length > 0 ? mergedHeaders : initReq?.headers,
    };
};

const buildRetryInitReq = (initReq: any, accessToken: string): any => ({
    ...(initReq ?? {}),
    headers: {
        ...headersToRecord(initReq?.headers),
        Authorization: `Bearer ${accessToken}`,
    },
});

const getErrorField = (error: unknown, field: string): unknown => {
    if (typeof error !== 'object' || error === null) return undefined;

    const direct = error as Record<string, unknown>;
    if (direct[field] !== undefined) return direct[field];

    const nested = direct.error;
    if (typeof nested === 'object' && nested !== null) {
        return (nested as Record<string, unknown>)[field];
    }

    return undefined;
};

const isUnauthorizedError = (error: unknown): boolean => {
    if (typeof Response !== 'undefined' && error instanceof Response) {
        return error.status === 401;
    }

    const status =
        getErrorField(error, 'status') ?? getErrorField(error, 'statusCode');
    if (Number(status) === 401) return true;

    const code = getErrorField(error, 'code');
    if (Number(code) === 16) return true;

    const message = getErrorField(error, 'message');
    return (
        typeof message === 'string' &&
        /unauthenticated|unauthorized|try refresh token/i.test(message)
    );
};

type RefreshedTokens = {
    accessToken: string;
    frontToken: string;
    refreshToken?: string;
};

// CamelCase to snake_case
// expect to define T for the input
type CamelToSnakeCase<S extends string> = S extends `${infer T}${infer U}`
    ? `${T extends Capitalize<T> ? '_' : ''}${Lowercase<T>}${CamelToSnakeCase<U>}`
    : S;

type CamelToSnakeCaseObject<T> = {
    [K in keyof T as CamelToSnakeCase<Extract<K, string>>]: T[K] extends object
        ? CamelToSnakeCaseObject<T[K]>
        : T[K];
};

export function camelToSnakeCase<T>(obj: T): CamelToSnakeCaseObject<T> {
    const toSnakeCase = (str: string) =>
        str.replace(/([A-Z]+)(?=[A-Z][a-z]|[0-9]|$)|([A-Z])/g, (match, p1, p2) => {
            // Special case for "ID"
            if (match === 'ID') return '_id';
            if (p1) return `_${p1.toLowerCase()}`;
            if (p2) return `_${p2.toLowerCase()}`;
            return '';
        });

    const isObject = (val: unknown): val is object =>
        val !== null && typeof val === 'object';

    const convert = (input: any): any => {
        if (!isObject(input)) {
            return input;
        }

        if (Array.isArray(input)) {
            return input.map(convert);
        }

        return Object.keys(input).reduce((acc, key) => {
            const newKey = toSnakeCase(key);
            acc[newKey] = convert((input as any)[key]);
            return acc;
        }, {} as Record<string, unknown>);
    };

    return convert(obj) as CamelToSnakeCaseObject<T>;
}

type SnakeToCamelCase<S extends string> = S extends `${infer T}_${infer U}`
    ? `${T}${Capitalize<SnakeToCamelCase<U>>}`
    : S;

export function snakeToCamelCase<T>(obj: any): T {
    const toCamelCase = (str: string) =>
        str.replace(/_id|_[a-z]/g, (match) => {
            // Special case for "_id"
            if (match === '_id') return 'ID';
            return match[1].toUpperCase();
        });

    const isObject = (val: unknown): val is object =>
        val !== null && typeof val === 'object';

    const convert = (input: any): any => {
        if (!isObject(input)) {
            return input;
        }

        if (Array.isArray(input)) {
            return input.map(convert);
        }

        return Object.keys(input).reduce((acc, key) => {
            const newKey = toCamelCase(key);
            acc[newKey] = convert((input as any)[key]);
            return acc;
        }, {} as Record<string, unknown>);
    };

    return convert(obj) as T;
}

const getRefreshToken = (cookieName = 'refreshToken'): string | null => {
    const match = document.cookie.match(
        new RegExp(`(?:^|;\\s*)${cookieName}=([^;]*)`)
    );
    return match ? decodeURIComponent(match[1]) : null;
};

/**
 * Thrown when refresh fails unrecoverably (unauthorized / token theft, or no
 * refresh token to use) — callers should log out. Transient failures
 * (network / 5xx) throw a plain Error instead, which callers may retry.
 */
export class SessionExpiredError extends Error {
    constructor(message = 'Session expired') {
        super(message);
        this.name = 'SessionExpiredError';
    }
}

/**
 * Clear all auth cookies, do it for short-circuit locally instead of replaying a dead token.
 */
const clearAuthCookies = (
    names: string[] = ['accessToken', 'frontToken', 'refreshToken']
): void => {
    names.forEach((name) => {
        document.cookie = buildCookieString(name, '', 0);
    });
};

const FRESH_ACCESS_TOKEN_MIN_SECONDS = 5;

/**
 * Perform one refresh against nara. Runs under the lock/dedup guarantee of
 * doRefreshToken. Persists all three tokens before returning
 */
async function refreshOnce(): Promise<RefreshedTokens> {
    // Freshness re-check: if another tab refreshed while we waited on the lock,
    // the access cookie now holds a fresh token — reuse it, no network call.
    const currentAccess = getCookie('accessToken');
    if (
        currentAccess &&
        getJWTRemainingSeconds(currentAccess) > FRESH_ACCESS_TOKEN_MIN_SECONDS
    ) {
        return {
            accessToken: currentAccess,
            frontToken: getCookie('frontToken') ?? '',
            refreshToken: getRefreshToken() ?? undefined,
        };
    }

    const refreshToken = getRefreshToken();
    if (!refreshToken) throw new SessionExpiredError('No refresh token available');

    const response = await fetch(`${config.naraBackendURI}/session/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
    });

    // 401 = token theft / unauthorized: the session is dead — terminal, no retry.
    if (response.status === 401) {
        clearAuthCookies();
        throw new SessionExpiredError();
    }
    // Network / 5xx: transient — plain Error so callers may retry.
    if (!response.ok) throw new Error('Failed to refresh token');

    const tokens = snakeToCamelCase<RefreshedTokens>(await response.json());
    // Persist all tokens before the lock releases, so a queued tab's freshness
    // re-check sees them. React callers re-write access/front via setCookie for
    // react-cookie reactivity; re-writing the same value is harmless.
    const accessLife = getJWTRemainingSeconds(tokens.accessToken);
    document.cookie = buildCookieString(
        'accessToken',
        tokens.accessToken,
        accessLife
    );
    document.cookie = buildCookieString('frontToken', tokens.frontToken, accessLife);
    if (tokens.refreshToken) {
        document.cookie = buildCookieString(
            'refreshToken',
            tokens.refreshToken,
            REFRESH_TOKEN_MAX_AGE
        );
    }
    return tokens;
}

/**
 * Single-flight token refresh. The in-flight promise dedupes concurrent
 * refreshes within this tab; the Web Locks API (when available) additionally
 * serializes refreshes across ALL tabs. Falls back to tab-local dedup only when
 * Web Locks is unavailable (older browsers / non-secure contexts).
 */
let refreshPromise: Promise<RefreshedTokens> | null = null;

function doRefreshToken(): Promise<RefreshedTokens> {
    if (refreshPromise) return refreshPromise;

    const run =
        typeof navigator !== 'undefined' && navigator.locks
            ? // Promise.resolve flattens the lock callback's promise (the lib types
              // it as returning the callback value directly rather than awaiting it).
              (Promise.resolve(
                  navigator.locks.request('nara-refresh:refreshToken', refreshOnce)
              ) as Promise<RefreshedTokens>)
            : refreshOnce();

    refreshPromise = run.finally(() => {
        refreshPromise = null;
    });
    return refreshPromise;
}

export { doRefreshToken };

const createProxy = (service: any): any => {
    const handler = {
        get(target: any, prop: any) {
            if (target[prop] && typeof target[prop] === 'function') {
                return async (...args: any[]) => {
                    let initReq: any | undefined;
                    const requestArgs = [...args];
                    if (requestArgs.length > 0) {
                        const lastArg = requestArgs[requestArgs.length - 1];
                        if (
                            lastArg &&
                            typeof lastArg === 'object' &&
                            ('pathPrefix' in lastArg ||
                                'headers' in lastArg ||
                                'method' in lastArg)
                        ) {
                            initReq = requestArgs.pop();
                        }
                    }
                    try {
                        const response = await target[prop](
                            ...requestArgs,
                            buildInitReq(initReq)
                        );
                        return checkError(response);
                    } catch (error) {
                        if (!isUnauthorizedError(error)) {
                            throw error;
                        }

                        let newTokens: RefreshedTokens;
                        try {
                            // refreshOnce persists the new cookies under the lock.
                            newTokens = await doRefreshToken();
                        } catch {
                            throw error;
                        }

                        const response = await target[prop](
                            ...requestArgs,
                            buildInitReq(
                                buildRetryInitReq(initReq, newTokens.accessToken)
                            )
                        );
                        return checkError(response);
                    }
                };
            }
            return target[prop];
        },
    };
    return new Proxy(service, handler);
};

export const PaulaClient = createProxy(WrappedPaula);

export const ScheduleV2Client = createProxy(WrappedScheduleV2);

export const PythagorasV2Client = createProxy(WrappedPythagoras);

export const LevelTrackerClient = createProxy(WrappedLevelTracker);

export const StudentTrackerClient = createProxy(WrappedStudentTracker);

export const TeacherFeedbackV3Client = createProxy(WrappedTeacherFeedbackV3);

export const LearningSummaryClient = createProxy(WrappedLearningSummary);

export const TeacherFeedbackMathClient = createProxy(WrappedTeacherFeedbackMath);

export const BookTrackerClient = createProxy(WrappedBookTracker);

/**
 * Wrapper around fetch that attaches the nara accessToken and retries once on 401.
 * On 401, refreshes the token via nara, updates cookies, and retries the request.
 * Uses the singleton refresh mutex to deduplicate concurrent 401 retries.
 */
export const authenticatedFetch = async (
    url: string,
    init?: FetchInit
): Promise<Response> => {
    const accessToken = getCookie('accessToken');
    const headers: Record<string, string> = {
        ...headersToRecord(init?.headers),
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    };

    const response = await fetch(url, { ...init, headers });

    if (response.status !== 401) return response;

    try {
        const newTokens = await doRefreshToken();

        return fetch(url, {
            ...init,
            headers: {
                ...headersToRecord(init?.headers),
                Authorization: `Bearer ${newTokens.accessToken}`,
            },
        });
    } catch {
        return response;
    }
};

export const authenticatedFetchRequest = async <R,>(
    path: string,
    init?: FetchInit & { pathPrefix?: string }
): Promise<R> => {
    const { pathPrefix, ...requestInit } = init ?? {};
    const url = pathPrefix ? `${pathPrefix}${path}` : path;

    const response = await authenticatedFetch(url, requestInit);
    const body = await response.json().catch(() => {
        throw response;
    });

    if (!response.ok) throw body;
    return body as R;
};
